/**
 * CreateSlideoutProvider — the single root for every CRM create surface.
 *
 * Keeps a stack of `CrmCreateConfig`s and renders ONE `SlideoutMenu` for the
 * whole stack: the top entry is visible, entries beneath it stay mounted
 * (hidden) so a nested "Create new X" launched from an association picker
 * returns to a parent whose typed values, drafts and acknowledged conflicts
 * are intact.
 *
 * Stacking with other overlays: the provider registers with
 * `useOverlayOrder` on every push, so a create form opened from inside a
 * record slideout lands above it, and a record slideout pushed from inside a
 * create form (conflict links, `onCreated`) lands above that. The
 * `SlideoutMenu` is modal, so the record beneath is inert while the form is
 * open. Each entry's `data-instance` is `create-<kind>` for e2e.
 *
 * The API consumers call lives in `./CreateSlideoutContext`.
 */

import {
  type ReactNode,
  useCallback,
  useEffectEvent,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useOverlayOrder } from '@/contexts/OverlayOrderContext';
import { StackingLevelProvider } from '@/contexts/StackingContext';
import {
  DEFAULT_SLIDEOUT_WIDTH,
  SlideoutMenu,
} from '@/lib/base/overlays/slideout-menu';
import { UnsavedChangesModal } from '@/lib/base/overlays/unsaved-changes-modal';
import { useSlideoutDismissGuard } from '@/lib/base/overlays/use-slideout-dismiss-guard';
import { cx } from '@/utils/cx';

import {
  CreateEntryGuardContext,
  type CreateEntryGuardRegistration,
  CreateEntryIsTopContext,
  CreateSlideoutContext,
  type CreateSlideoutContextValue,
  type OpenCreateSlideoutOptions,
} from './CreateSlideoutContext';
import { createSlideoutInstance } from './createSlideoutInstance';
import { CrmCreateForm } from './CrmCreateForm';
import type { CrmCreateConfig } from './types';

interface CreateSlideoutEntry {
  id: number;
  config: CrmCreateConfig;
  onResult?: (result: unknown) => void;
}

/** No unsaved entries. Hoisted so the initial state is reference-stable. */
const NO_DIRTY_ENTRIES: ReadonlySet<number> = new Set<number>();

/**
 * Discarding an entry needs no cleanup: it unmounts on pop and its form
 * state goes with it. The guard still requires a discard callback.
 */
const noop = () => {};

/** Resizable range for create forms; wide enough for long labels, capped so
 *  a single column never sprawls. */
const CREATE_SLIDEOUT_MIN_WIDTH = 400;
const CREATE_SLIDEOUT_MAX_WIDTH = 720;

/** Direction of the last stack operation, used for entry animations. */
type StackDirection = 'push' | 'pop' | 'none';

// ---------------------------------------------------------------------------
// Entry wrapper
// ---------------------------------------------------------------------------

function CreateSlideoutEntryView({
  entry,
  isTop,
  direction,
  setEntryDirty,
}: {
  entry: CreateSlideoutEntry;
  isTop: boolean;
  /** Direction of the operation that made this entry the top, or 'none'. */
  direction: StackDirection;
  setEntryDirty: (entryId: number, isDirty: boolean) => void;
}) {
  const guardRegistration = useMemo<CreateEntryGuardRegistration>(
    () => ({ entryId: entry.id, setEntryDirty }),
    [entry.id, setEntryDirty]
  );

  return (
    <CreateEntryIsTopContext value={isTop}>
      <CreateEntryGuardContext value={guardRegistration}>
        {/* Hidden (not unmounted) beneath a child so the parent's form state
            survives the round trip. `<Activity>` is deliberately not used:
            pausing the parent's effects would drop an in-flight deal
            auto-fill result on the floor via its unmount flag. */}
        {/* NOT the shared SLIDEOUT_ANIM_* constants: those are react-aria
            `data-[entering]/[exiting]` full-panel right-edge slides, while this
            is a mount-driven push/pop of content WITHIN an already-open panel,
            and the pop direction (from-left) has no shared constant at all.
            Kept local, but on the house pane-slide budget (300ms) and gated
            with `motion-safe:` per VISUAL_STYLE_GUIDE.md § Motion. */}
        <div
          className={cx(
            isTop ? 'flex size-full min-h-0 flex-1 flex-col' : 'hidden',
            isTop &&
              direction === 'push' &&
              'motion-safe:animate-in motion-safe:slide-in-from-right-4 motion-safe:fade-in motion-safe:duration-300',
            isTop &&
              direction === 'pop' &&
              'motion-safe:animate-in motion-safe:slide-in-from-left-4 motion-safe:fade-in motion-safe:duration-300'
          )}
        >
          <CrmCreateForm config={entry.config} />
        </div>
      </CreateEntryGuardContext>
    </CreateEntryIsTopContext>
  );
}

// ---------------------------------------------------------------------------
// Overlay (inside StackingLevelProvider so z-index follows open order)
// ---------------------------------------------------------------------------

function CreateSlideoutOverlay({
  stack,
  requestClose,
  setEntryDirty,
  direction,
}: {
  stack: CreateSlideoutEntry[];
  requestClose: () => void;
  setEntryDirty: (entryId: number, isDirty: boolean) => void;
  direction: StackDirection;
}) {
  const topEntry = stack[stack.length - 1];

  // Escape and outside click both land here. Neither may discard the user's
  // work without asking — requestClose consults the top entry's registration.
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        requestClose();
      }
    },
    [requestClose]
  );

  // Only an entry a push/pop just revealed animates; the first entry renders
  // in place with the panel's own enter transition.
  const animatedDirection: StackDirection =
    stack.length > 1 || direction === 'pop' ? direction : 'none';

  return (
    <SlideoutMenu
      isOpen
      onOpenChange={handleOpenChange}
      instance={createSlideoutInstance(topEntry.config.entityType)}
      dialogAriaLabel={topEntry.config.title}
      resizable
      initialWidth={DEFAULT_SLIDEOUT_WIDTH}
      minWidth={CREATE_SLIDEOUT_MIN_WIDTH}
      maxWidth={CREATE_SLIDEOUT_MAX_WIDTH}
      onBack={stack.length > 1 ? requestClose : undefined}
    >
      {stack.map((entry, i) => (
        <CreateSlideoutEntryView
          key={entry.id}
          entry={entry}
          isTop={i === stack.length - 1}
          direction={animatedDirection}
          setEntryDirty={setEntryDirty}
        />
      ))}
    </SlideoutMenu>
  );
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function CreateSlideoutProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<CreateSlideoutEntry[]>([]);
  const [direction, setDirection] = useState<StackDirection>('none');
  const nextIdRef = useRef(0);
  // Increments on each push to force useOverlayOrder re-registration, so a
  // create form opened from inside a record slideout stacks above it.
  const stackVersionRef = useRef(0);

  const overlayOrder = useOverlayOrder(
    stack.length > 0 ? stackVersionRef.current : 0
  );

  const open = useCallback(
    (config: CrmCreateConfig, options?: OpenCreateSlideoutOptions) => {
      nextIdRef.current += 1;
      stackVersionRef.current += 1;
      const entry: CreateSlideoutEntry = {
        id: nextIdRef.current,
        config,
        onResult: options?.onResult,
      };
      setDirection('push');
      setStack(prev => [...prev, entry]);
    },
    []
  );

  const pop = useCallback((result?: unknown) => {
    setDirection('pop');
    setStack(prev => {
      if (prev.length === 0) {
        return prev;
      }
      const top = prev[prev.length - 1];
      // Fire onResult asynchronously to avoid setState-during-render.
      if (top.onResult && result !== undefined) {
        queueMicrotask(() => top.onResult?.(result));
      }
      return prev.slice(0, -1);
    });
  }, []);

  // Which entries have reported unsaved user input. Keyed by entry id rather
  // than a single "top is dirty" flag so that popping a dirty child back to a
  // dirty parent re-arms the guard for the parent immediately, with no window
  // in which a second Escape slips through.
  const [dirtyEntryIds, setDirtyEntryIds] =
    useState<ReadonlySet<number>>(NO_DIRTY_ENTRIES);

  const setEntryDirty = useCallback((entryId: number, isDirty: boolean) => {
    setDirtyEntryIds(prev => {
      if (prev.has(entryId) === isDirty) {
        return prev;
      }
      const next = new Set(prev);
      if (isDirty) {
        next.add(entryId);
      } else {
        next.delete(entryId);
      }
      return next;
    });
  }, []);

  const topEntry = stack.length > 0 ? stack[stack.length - 1] : undefined;
  const isTopEntryDirty =
    topEntry !== undefined && dirtyEntryIds.has(topEntry.id);

  // No onSave: these are create forms. Offering "Save changes" from a
  // confirmation dialog would either create a record from an Escape keypress
  // or have to surface field validation errors from inside the dialog.
  const { guard, modalProps } = useSlideoutDismissGuard({
    hasUnsavedChanges: isTopEntryDirty,
    onDiscard: noop,
  });

  // `guard` is rebuilt (new identity) by useSlideoutDismissGuard's own
  // useCallback whenever `hasUnsavedChanges` (isTopEntryDirty) toggles. If
  // requestClose depended on `guard` directly, every consumer of
  // CreateSlideoutContext would re-render on that transition (see
  // CreateSlideoutContextValue's memo below). An Effect Event reads the
  // latest `guard`/`pop` without being a reactive dependency, so
  // requestClose itself keeps a stable identity — this is the sanctioned
  // latest-value pattern (REACT_19_PATTERNS.md § 4.1), not a ref write
  // during render.
  const requestCloseEvent = useEffectEvent(() => {
    if (guard(pop)) {
      pop();
    }
  });

  const requestClose = useCallback(() => {
    requestCloseEvent();
    // requestCloseEvent is an Effect Event: always sees the latest guard/pop
    // without being a reactive dependency (REACT_19_PATTERNS.md § 4.1).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const titles = useMemo(() => stack.map(entry => entry.config.title), [stack]);

  const value = useMemo<CreateSlideoutContextValue>(
    () => ({
      open,
      pop,
      requestClose,
      isOpen: stack.length > 0,
      depth: stack.length,
      titles,
    }),
    [open, pop, requestClose, stack.length, titles]
  );

  const stackLevel = overlayOrder || 1;

  return (
    <CreateSlideoutContext value={value}>
      {children}

      {stack.length > 0 && (
        <>
          {/* One level above the stack so the confirmation sits over the
              form it is asking about, not behind it. */}
          <StackingLevelProvider level={stackLevel + 1}>
            <UnsavedChangesModal {...modalProps} />
          </StackingLevelProvider>
          <StackingLevelProvider level={stackLevel}>
            <CreateSlideoutOverlay
              stack={stack}
              requestClose={requestClose}
              setEntryDirty={setEntryDirty}
              direction={direction}
            />
          </StackingLevelProvider>
        </>
      )}
    </CreateSlideoutContext>
  );
}
